"use client";

// The help & feedback launcher — lives in the header beside the operator
// button. The glyph is a speech bubble with a centred dot — the shared
// line-icon language used across the app's icon-circle buttons (see
// SignOutButton.tsx / DashboardClient.tsx dev-mode toggle: 24x24 viewBox,
// stroke="currentColor", strokeWidth 1.6, round caps/joins, no fill), rather
// than a bespoke organism-eye shape. It also owns the global paste listener:
// pasting an image/video anywhere (modal closed, target not an input) pops
// the sheet open with the file attached. Styles ride along in a co-located
// <style> tag (the DirIcon DIRICON_SCALE_CSS pattern), nothing in globals; the
// sheet itself (ground, frost, padding, motion) is EtherModal's, not ours.

import { useEffect, useRef, useState } from "react";
import FeedbackModal from "./FeedbackModal";
import FeedbackMark from "../brand/FeedbackMark";
import { addDraftFiles } from "../../lib/feedback/draftStore";

const FEEDBACK_CSS = `
.fb-wrap{position:relative;display:inline-flex}
/* the sheet is THE sheet (EtherModal): its ground, its one frost (the veil), its
   padding and its animation are the sheet's own. Only what lies inside it is
   styled here. The body is one column the writing field grows to fill, so the
   fixed height never leaves a hollow. */
.sheet-body.fb-sheet{display:flex;flex-direction:column;min-height:100%}
.fb-body{position:relative;display:flex;flex:1 1 auto;flex-direction:column;gap:1.15rem;min-height:0}
/* whose report it is, quiet at the foot's left */
.fb-who{font-size:0.86rem;color:var(--ink-3);letter-spacing:-0.01em;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
/* the message field rides THE one text field (.input-soft). It takes the room
   the rest leaves, never less than a few lines, and never drags a corner. */
.fb-text.input-soft{flex:1 1 7rem;min-height:7rem;max-height:none;field-sizing:fixed;line-height:1.6;resize:none}
.fb-chips{display:flex;gap:0.5rem;flex-wrap:wrap}
.fb-chips .chip{white-space:nowrap;padding:0.4rem 0.95rem;min-height:36px}
/* dropzone: a centred, iconed panel rather than a bare text stub */
.fb-drop{display:flex;flex-direction:column;align-items:center;justify-content:center;gap:0.4rem;width:100%;padding:1.15rem 1rem;border:1.5px dashed var(--hair-soft);border-radius:10px;background:color-mix(in oklab, var(--surface) 45%, transparent);color:var(--ink-3);font:inherit;font-size:0.9rem;text-align:center;cursor:pointer;transition:color var(--dur-fast) ease, border-color var(--dur-fast) ease, background var(--dur-fast) ease}
.fb-drop:hover{color:var(--ink-2);border-color:var(--ink-3);background:color-mix(in oklab, var(--surface) 68%, transparent)}
.fb-drop[data-has="true"]{border-style:solid;color:var(--ink-2)}
.fb-drop-ico{color:var(--ink-3);opacity:0.9}
.fb-drop-hint{font-size:0.8rem;color:var(--ink-4)}
.fb-previews{display:flex;gap:0.6rem;flex-wrap:wrap}
.fb-thumb{position:relative;width:80px;height:80px;border-radius:6px;overflow:hidden;border:1px solid var(--hair-soft);background:var(--surface)}
.fb-thumb img,.fb-thumb video{width:100%;height:100%;object-fit:cover;display:block}
.fb-thumb-x{position:absolute;top:4px;right:4px;width:24px;height:24px;display:grid;place-items:center;padding:0;border:none;border-radius:999px;background:color-mix(in oklab, var(--ink) 55%, transparent);color:var(--bg);font-size:15px;line-height:1;cursor:pointer}
.fb-note{margin:0;font-size:0.85rem;color:var(--ink-3)}
.fb-note--warn{color:var(--danger,#c0392b)}
/* the send pill keeps one width through "send it" and "it's with us" */
.eth-foot .fb-send{min-width:9.5rem}
.fb-dropveil{position:absolute;inset:-0.4rem;z-index:3;display:grid;place-items:center;border:1.5px dashed color-mix(in oklab, var(--organism-color) 70%, var(--ink-3));border-radius:10px;background:color-mix(in oklab, var(--organism-color) 12%, var(--bg));color:var(--ink-2);font-size:0.95rem;pointer-events:none}
@media (prefers-reduced-motion: reduce){.fb-text,.fb-drop{transition:none}}
`;

function isEditable(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  return !!el?.closest?.('input, textarea, [contenteditable="true"], [contenteditable=""]');
}

export default function FeedbackLauncher({ userName }: { userName?: string }) {
  const [open, setOpen] = useState(false);
  const openRef = useRef(open);
  openRef.current = open;

  // pasting a screenshot while the sheet is CLOSED brings it back with the
  // image attached — the "went to grab a screenshot" path. The modal owns
  // paste while open.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (openRef.current) return;
      const picked = Array.from(e.clipboardData?.files ?? []).filter((f) =>
        /^(image|video)\//.test(f.type),
      );
      if (!picked.length || isEditable(e.target)) return;
      const { added } = addDraftFiles(picked);
      if (added > 0) {
        e.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, []);

  return (
    <span className="fb-wrap">
      <style>{FEEDBACK_CSS}</style>
      <button
        type="button"
        className="icon-circle os-headbtn"
        aria-label="help & feedback"
        aria-expanded={open}
        data-noswipe
        onClick={() => setOpen((v) => !v)}
      >
        <FeedbackMark size={19} />
      </button>
      <FeedbackModal open={open} onClose={() => setOpen(false)} userName={userName} />
    </span>
  );
}
